<template>
  <div class="slot">
    <!-- 잔액/배팅 (데모) -->
    <div class="slot__top">
      <div class="slot__balance">🥕 {{ balance.toLocaleString() }}</div>
      <div class="slot__bet">
        <span class="slot__bet-label">배팅</span>
        <v-btn v-for="b in betOptions" :key="b" x-small rounded :outlined="bet !== b" :color="bet === b ? '#c98a2e' : ''"
          :class="{ 'white--text': bet === b }" :disabled="spinning" @click="bet = b">{{ b }}</v-btn>
      </div>
    </div>

    <!-- 릴 3개 -->
    <div class="slot__reels">
      <div v-for="(sym, i) in reels" :key="i" class="reel" :class="{ spinning }">
        <span class="reel__sym">{{ sym.name }}</span>
      </div>
    </div>

    <!-- 결과 -->
    <div class="slot__result" :class="{ win: lastWin > 0 }">{{ message || ' ' }}</div>

    <!-- 스핀 -->
    <v-btn class="slot__spin" rounded :loading="spinning" :disabled="balance < bet" @click="spin">
      <span>스핀 · 🥕 {{ bet }}</span>
    </v-btn>

    <!-- 배당표 -->
    <div class="slot__paytable">
      <span v-for="s in symbols" :key="s.id" class="pay">{{ s.name }}×3 = ×{{ s.value }}</span>
    </div>
  </div>
</template>

<script>
// slots-js의 심볼 구조 차용 { id, name(이모지), value(3매치 배수), weight(출현 가중치) }
const SYMBOLS = [
  { id: "carrot", name: "🥕", value: 3, weight: 30 },
  { id: "rabbit", name: "🐰", value: 4, weight: 22 },
  { id: "fox", name: "🦊", value: 5, weight: 16 },
  { id: "panda", name: "🐼", value: 7, weight: 12 },
  { id: "tiger", name: "🐯", value: 10, weight: 8 },
  { id: "star", name: "⭐", value: 15, weight: 5 },
  { id: "seven", name: "7️⃣", value: 30, weight: 3 }, // 잭팟
];

export default {
  name: "SlotMachine",
  data() {
    return {
      symbols: SYMBOLS,
      reels: [SYMBOLS[0], SYMBOLS[1], SYMBOLS[2]],
      spinning: false,
      balance: 500, // 데모 잔액 (추후 currency 연동)
      bet: 50,
      betOptions: [10, 50, 100],
      lastWin: 0,
      message: "",
      _timers: [],
    };
  },
  beforeDestroy() {
    this._timers.forEach((t) => clearInterval(t));
  },
  methods: {
    randomSym() {
      return SYMBOLS[Math.floor(Math.random() * SYMBOLS.length)];
    },
    // 가중치 추첨 (실제 결과)
    weightedPick() {
      const total = SYMBOLS.reduce((s, x) => s + x.weight, 0);
      let r = Math.random() * total;
      for (const s of SYMBOLS) {
        if ((r -= s.weight) <= 0) return s;
      }
      return SYMBOLS[0];
    },
    spin() {
      if (this.spinning || this.balance < this.bet) return;
      this.balance -= this.bet;
      this.lastWin = 0;
      this.message = "";
      this.spinning = true;

      const targets = [this.weightedPick(), this.weightedPick(), this.weightedPick()];

      // 각 릴을 빠르게 깜빡이게
      this._timers = this.reels.map((_, i) =>
        setInterval(() => this.$set(this.reels, i, this.randomSym()), 60),
      );

      // 스태거 정지
      const stopReel = (i, sym) => {
        clearInterval(this._timers[i]);
        this.$set(this.reels, i, sym);
      };
      setTimeout(() => stopReel(0, targets[0]), 700);
      setTimeout(() => stopReel(1, targets[1]), 1000);
      setTimeout(() => {
        stopReel(2, targets[2]);
        this.evaluate(targets);
      }, 1300);
    },
    evaluate([a, b, c]) {
      this.spinning = false;
      let win = 0;
      if (a.id === b.id && b.id === c.id) {
        win = a.value * this.bet; // 3매치
        this.message = a.id === "seven" ? `🎉 잭팟! ${a.name}${a.name}${a.name}` : `🎉 ${a.name} 3매치! +${win}`;
      } else if (a.id === b.id || b.id === c.id || a.id === c.id) {
        win = Math.floor(this.bet * 0.5); // 2매치 위로상금
        this.message = `✨ 2매치 +${win}`;
      } else {
        this.message = "꽝! 다시 도전 🍀";
      }
      this.balance += win;
      this.lastWin = win;
      // 추후: currency Edge Function으로 차감/배당 반영 (지금은 데모 잔액)
    },
  },
};
</script>

<style scoped>
.slot {
  max-width: 420px;
  margin: 0 auto 24px;
  padding: 20px;
  border-radius: 20px;
  background: linear-gradient(135deg, #fff4e0, #ffe6c2);
  border: 1px solid #f1c98a;
  box-shadow: 0 8px 24px rgba(201, 138, 46, 0.18);
}
.slot__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
}
.slot__balance {
  font-weight: 800;
  font-size: 18px;
  color: #8a5d13;
}
.slot__bet {
  display: flex;
  align-items: center;
  gap: 4px;
}
.slot__bet-label {
  font-size: 12px;
  color: #9a6a18;
  margin-right: 4px;
}

.slot__reels {
  display: flex;
  gap: 10px;
  justify-content: center;
}
.reel {
  width: 96px;
  height: 110px;
  border-radius: 14px;
  background: #fffdf8;
  border: 2px solid #e9c98c;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: inset 0 4px 12px rgba(0, 0, 0, 0.06);
  overflow: hidden;
}
.reel__sym {
  font-size: 56px;
  line-height: 1;
}
.reel.spinning .reel__sym {
  animation: blur 0.12s linear infinite;
}
@keyframes blur {
  0% { transform: translateY(-4px); opacity: 0.7; }
  50% { transform: translateY(0); opacity: 1; }
  100% { transform: translateY(4px); opacity: 0.7; }
}

.slot__result {
  text-align: center;
  min-height: 26px;
  margin: 12px 0;
  font-weight: 800;
  font-size: 16px;
  color: #8a8699;
}
.slot__result.win {
  color: #e0892b;
}

.slot__spin {
  display: block;
  width: 100%;
  height: 52px !important;
  font-size: 18px !important;
  font-weight: 800 !important;
  color: #fff !important;
  background: linear-gradient(135deg, #f0a93b, #e8743b) !important;
  box-shadow: 0 8px 20px rgba(232, 116, 59, 0.4) !important;
}

.slot__paytable {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 12px;
  justify-content: center;
  margin-top: 14px;
}
.pay {
  font-size: 11px;
  color: #a07a3a;
  font-weight: 600;
}
</style>
